import { Box } from '@chakra-ui/react';
import { RootState, useAppSelector, useAppStore } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { selectActiveWidgetType, selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { ReactElement, memo } from 'react';
import { WidgetType } from 'types/widget.types';
import { DEFAULT_MARGIN_STEPPED_LINE } from 'widgets/LineWidget/LineWidget.config';
import { mergePosList } from 'widgets/LineWidget/LineWidget.helpers';
import { LineWidgetData, Rect } from 'widgets/LineWidget/LineWidget.types';
import { PointGuidesForSteps } from 'widgets/LineWidget/LineWidget.types';
import { generateGridPointsFromWidgets, roundPos } from 'widgets/LineWidget/StepsLineWidget/StepsLineWidget.helpers';

import { useCurrentLineData } from './PortStepsGrid.hooks';

const CIRCLE_WIDTH = 30;

/**
 * Component renders grid points for stepped lines, purely for debug purposes.
 * If a line is selected, all the possible points that could be used in the
 * stepped line are rendered for easy debugging.
 * NOTE: component does not render on production. Must be manually enabled.
 */
function PortStepsGridRenderer({
  realTimeStartRect,
  realTimeEndRect,
}: {
  realTimeStartRect?: Rect | null;
  realTimeEndRect?: Rect | null;
}): ReactElement {
  const store = useAppStore();
  const storeState = store.getState() as RootState;
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const currentType = useAppSelector(selectActiveWidgetType);
  const currentWidget = activeWidgets ? activeWidgets[0] : null;
  const currentWidgetId = currentWidget ? currentWidget.id : null;
  const currentWidgetData = currentWidgetId ? selectWidget(currentWidgetId)(storeState) : null;
  const lineWidget = currentType === WidgetType.Line ? (currentWidgetData as LineWidgetData) : null;

  const posList = lineWidget
    ? mergePosList(roundPos(lineWidget.startPos), lineWidget.midPosList, roundPos(lineWidget.endPos))
    : [];
  const { relativeStartRect, relativeEndRect, startSide, endSide } = useCurrentLineData(
    !!lineWidget
      ? {
          startPort: lineWidget.startPort,
          endPort: lineWidget.endPort,
          leftPx: lineWidget.leftPx,
          topPx: lineWidget.topPx,
          posList,
        }
      : null,
    realTimeStartRect,
    realTimeEndRect,
  );

  const startPos1 = posList[0]; // TODO: get value from flow context
  const endPos1 = posList[1]; // TODO: get value from flow context
  const stepsPathsDebug =
    !!startPos1 && !!endPos1
      ? generateGridPointsFromWidgets(
          relativeStartRect,
          relativeEndRect,
          startSide,
          endSide,
          roundPos(startPos1),
          roundPos(endPos1),
          DEFAULT_MARGIN_STEPPED_LINE,
        )
      : null;

  const fakePoints: PointGuidesForSteps = stepsPathsDebug
    ? stepsPathsDebug
    : { gridPoints: [], normalPoints: [], points: [] };

  if (currentType === WidgetType.Line) {
    return (
      <Box position='absolute' top={0} left={0} width='100%' height='100%' pointerEvents='none' zIndex={999999999}>
        {/* Render start/end ports if widgets are connected to line */}
        {!!lineWidget?.startPort && (
          <Box
            position='absolute'
            width={`${relativeStartRect.width}px`}
            height={`${relativeStartRect.height}px`}
            border='3px solid orange'
            left={`${relativeStartRect.x}px`}
            top={`${relativeStartRect.y}px`}
            opacity={0.75}
          />
        )}

        {!!lineWidget?.endPort && (
          <Box
            position='absolute'
            width={`${relativeEndRect.width}px`}
            height={`${relativeEndRect.height}px`}
            border='3px solid orange'
            left={`${relativeEndRect.x}px`}
            top={`${relativeEndRect.y}px`}
            opacity={0.75}
          />
        )}

        {/* Render grid points stepped lines can travel to */}
        {fakePoints.gridPoints.map((point, index) => (
          <>
            <Box
              key={`grid-${index}`}
              position='absolute'
              width={`${CIRCLE_WIDTH}px`}
              height={`${CIRCLE_WIDTH}px`}
              borderRadius='50%'
              backgroundColor='blue'
              left={`${point.xPx - CIRCLE_WIDTH / 2}px`}
              top={`${point.yPx - CIRCLE_WIDTH / 2}px`}
              zIndex={999999999}
            />
            <div
              style={{
                position: 'absolute',
                left: `${point.xPx + 15}px`,
                top: `${point.yPx - CIRCLE_WIDTH / 2}px`,
                color: 'blue',
                fontSize: '0.8rem',
                zIndex: 999999999,
              }}
              key={`grid-${index}-text`}
            >
              ({point.xPx}, {point.yPx})
            </div>
          </>
        ))}

        {/* Render start/end of lines */}
        <Box
          position='absolute'
          width={`${CIRCLE_WIDTH / 2}px`}
          height={`${CIRCLE_WIDTH / 2}px`}
          backgroundColor='green'
          borderRadius='50%'
          left={`${startPos1.xPx - CIRCLE_WIDTH / 4}px`}
          top={`${startPos1.yPx - CIRCLE_WIDTH / 4}px`}
        />
        <Box
          position='absolute'
          width={`${CIRCLE_WIDTH / 2}px`}
          height={`${CIRCLE_WIDTH / 2}px`}
          backgroundColor='green'
          borderRadius='50%'
          left={`${endPos1.xPx - CIRCLE_WIDTH / 4}px`}
          top={`${endPos1.yPx - CIRCLE_WIDTH / 4}px`}
        />
      </Box>
    );
  }

  return <></>;
}

export default memo(PortStepsGridRenderer);
